class CarModelMove {
  lastTime = 0 //上一次移动的时间
  lastPosition //上一次的位置
  node //模型节点
  animation //动画相关
  isFirst = true //用于第一次初始化
  isReset = false //用于动画没正常播放后再次重启
  startTime = 0 //动画开始时间
  /**
   * 动画路径点构造函数
   *
   * @param {Object} options.player
   * @param {Object} options.node 节点，车辆的节点
   * @param {Array<number>} options.position 车辆起始位置 [x, y, z]
   * @param {Array<number>} [options.forword=[1, 0, 0]] 车辆模型的朝向向量，默认为 [1, 0, 0]
   * @param {number} [options.minDuration=0] 移动时间最小间隔，功能等于节流，默认为0，不节流，单位为ms
   * @param {number} [options.delay=1000] 动画的延迟时间(毫秒)，默认为1000，为了让模型有足够的时间移动，防止移动动画完成后下一个数据还没有来导致动画终止，考虑现实可能数据长时间终止导致动画终止，这种情况就会触发重置
   */
  constructor({ player, node, position, forword = [1, 0, 0], minDuration = 0, delay = 1000 }) {
    this.player = player
    player.Native.Node.setProperty(node, { localTrans: position })
    this.startTime = this.lastTime = performance.now() - delay
    this.lastPosition = position
    this.node = node
    this.forword = forword
    this.minDuration = minDuration
    this.delay = delay
  }

  // 移动
  async moveTo(position) {
    const now = performance.now()
    const duration = now - this.lastTime
    if (this.minDuration !== 0 && duration < this.minDuration) return
    this.lastTime = now
    await this.setAnimation(position, duration / 1000)
  }
  //设置移动动画
  async setAnimation(position, duration) {
    const { length, quat } = this.getLineInfo(this.lastPosition, position, this.forword)

    if (this.isFirst) {
      const mixer = await this.player.Native?.AnimationMixer.getMixer(this.node + '_mixer')
      const clip = await this.player.Native?.AnimationClip.getClip(mixer, this.node + '_clip')
      const action = await this.player.Native?.AnimationAction.clipAction(mixer, clip)
      const EnumLMLoopOnce = 0 // 只执行一次
      await this.player.Native?.AnimationAction.setLoop(action, EnumLMLoopOnce, -1)
      this.animation = { mixer, clip, action, playing: true }

      // await this.this.player.Native?.AnimationAction.setEventAnimationUpdateDetail(true)
      // await this.player.Native?.AnimationAction.bindActionUpdate(this.animation.action)
    }
    if (this.isFirst || this.isReset) {
      const EnumKFTTReal3 = 3 // 三维向量(double3)类型
      const EnumAPBTTranslation = 1 // 位置属性绑定
      const EnumITLinear = 0 // 线性插值
      const trackT = await this.player.Native?.KeyframeTrack.addTrackWithParam(
        this.animation.clip,
        this.node,
        {
          name: this.node + '_trackLocalTranslation',
          trackType: EnumKFTTReal3,
          propertyLinkType: EnumAPBTTranslation,
          interpolationType: EnumITLinear,
        }
      )

      const EnumKFTTQuaternion = 5 // 四元数(double4)类型
      const EnumAPBTRotation = 2 // 旋转属性绑定
      const EnumITDiscrete = 2 // 离散插值
      const trackR = await this.player.Native?.KeyframeTrack.addTrackWithParam(
        this.animation.clip,
        this.node,
        {
          name: this.node + '_trackLocalRotation',
          trackType: EnumKFTTQuaternion,
          propertyLinkType: EnumAPBTRotation,
          interpolationType: 0,
        }
      )
      this.animation.trackR = trackR
      this.animation.trackT = trackT
      this.isReset = false
      const trackTList = [
        {
          keyframeTrack: this.animation.trackT,
          time: 0,
          value: this.lastPosition,
        },
        {
          keyframeTrack: this.animation.trackT,
          time: duration,
          value: position,
        },
        {
          keyframeTrack: this.animation.trackR,
          time: 0,
          value: [1, 0, 0, 0],
        },
        {
          keyframeTrack: this.animation.trackR,
          time: duration,
          value: quat,
        },
      ]
      await this.player.Native?.KeyframeTrack.batchKeyAppend(trackTList)
      this.player.Native?.AnimationAction.play(this.animation.action)
      this.isFirst = false
    } else {
      const trackTList = [
        {
          keyframeTrack: this.animation.trackT,
          time: (this.lastTime - this.startTime) * 0.001,
          value: position,
        },
        {
          keyframeTrack: this.animation.trackR,
          time: (this.lastTime - this.startTime) * 0.001,
          value: quat,
        },
      ]

      const isplay = await this.player.Native?.AnimationAction.isPlaying(this.animation.action)

      if (isplay) {
        await this.player.Native?.KeyframeTrack.batchKeyAppend(trackTList)
      } else {
        this.reset()
      }
    }

    this.lastPosition = position
  }

  // 重置
  reset() {
    this.player.Native?.KeyframeTrack.clearTrack(this.animation.clip)
    this.startTime = this.lastTime = performance.now() - this.delay
    this.isReset = true
  }
  // 删除节点
  async destroy() {
    await this.player.Native?.Node.destroy(this.node)
  }
  async setVisible(visible) {
    await this.player.Native?.Node.setProperty(this.node, { visible })
  }
  // 计算两个点A、B之间的距离、方向向量、以及转到该向量的四元数
  getLineInfo(A, B, towards = [1, 0, 0], onlyXY = true) {
    // 计算方向向量AB
    const vecAB = [B[0] - A[0], B[1] - A[1], B[2] - A[2]]
    // 计算向量长度
    const lengthAB = Math.sqrt(vecAB[0] ** 2 + vecAB[1] ** 2 + vecAB[2] ** 2)
    // 处理零向量情况（A和B重合）
    if (lengthAB < 1e-6) {
      return {
        length: lengthAB,
        quat: [1, 0, 0, 0],
      } // 返回单位四元数
    }

    // 归一化目标向量
    const v = [vecAB[0] / lengthAB, vecAB[1] / lengthAB, vecAB[2] / lengthAB]
    if (onlyXY) v[2] = 0
    // 原始向量 [1, 0, 0]
    const u = towards
    // 计算点积和叉积
    const dot = u[0] * v[0] + u[1] * v[1] + u[2] * v[2]
    let axis = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]]

    // 计算叉积长度
    const axisLength = Math.sqrt(axis[0] ** 2 + axis[1] ** 2 + axis[2] ** 2)
    // 处理平行向量（包括同向和反向）
    if (axisLength < 1e-6) {
      if (Math.abs(dot - 1) < 1e-6) {
        // 同向
        return {
          length: lengthAB,
          quat: [1, 0, 0, 0],
        }
      } else if (Math.abs(dot + 1) < 1e-6) {
        // 反向

        // 构造180度旋转四元数
        return {
          length: lengthAB,
          quat: [0, 0, 0, 1],
        }
      }
    }

    // 归一化旋转轴
    const axisNormalized = [axis[0] / axisLength, axis[1] / axisLength, axis[2] / axisLength]

    // 计算旋转角度
    const theta = Math.acos(dot)
    const halfTheta = theta / 2
    // 构造四元数
    return {
      length: lengthAB,
      direction: v,
      quat: [
        Math.cos(halfTheta),
        axisNormalized[0] * Math.sin(halfTheta),
        axisNormalized[1] * Math.sin(halfTheta),
        axisNormalized[2] * Math.sin(halfTheta),
      ],
    }
  }
}

export default CarModelMove
